import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Building2, ClipboardList, Inbox, KeyRound, LayoutGrid, Menu, Package, ScrollText, X } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Perlu tindakan", icon: Inbox, id: "nav-admin-queue" },
  { to: "/admin/peminjaman", label: "Peminjaman", icon: ClipboardList, id: "nav-admin-bookings" },
  { to: "/admin/lokasi", label: "Lokasi", icon: Building2, id: "nav-admin-locations" },
  { to: "/admin/inventaris", label: "Inventaris", icon: Package, id: "nav-admin-inventory" },
  { to: "/admin/akses", label: "Akses storage", icon: KeyRound, id: "nav-admin-access" },
  { to: "/admin/laporan", label: "Laporan", icon: LayoutGrid, id: "nav-admin-reports" },
  { to: "/admin/aktivitas", label: "Aktivitas", icon: ScrollText, id: "nav-admin-activity" },
];

// Shell admin terpisah dari shell user (Layout): tanpa katalog nav, cart,
// atau bottom nav. Sidebar 240px statis >=1280px, off-canvas di bawahnya.
// Hanya memakai Outfit + JetBrains Mono (maks 2 font di admin).
export default function AdminShell({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  // Drawer off-canvas: fokus awal ke tombol tutup, Escape menutup +
  // fokus kembali, Tab terperangkap di dalam drawer selama terbuka.
  useEffect(() => {
    if (!open) return undefined;
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        menuBtnRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const items = [...drawerRef.current.querySelectorAll("a[href], button:not([disabled])")]
        .filter((el) => el.getClientRects().length > 0);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open ]);

  const closeDrawer = () => {
    setOpen(false);
    menuBtnRef.current?.focus();
  };

  const nav = (
    <nav aria-label="Navigasi admin" className="flex flex-col gap-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.id}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <div className="flex">
        {/* Statis >=1280px */}
        <aside className="hidden w-60 shrink-0 flex-col border-r border-white/10 px-3 py-4 xl:flex min-h-screen sticky top-0 max-h-screen overflow-y-auto">
          <Link to="/" aria-label="Dexter - kembali ke Home" className="mb-4 flex items-center gap-2.5 px-2 py-1">
            <img src="/yn-logo.svg" alt="" className="h-8 w-8 shrink-0 object-contain" />
            <span className="leading-tight">
              <span className="block text-[15px] font-bold tracking-tight">Dexter</span>
              <span className="block text-xs text-zinc-500">Meja kendali peminjaman</span>
            </span>
          </Link>
          {nav}
          <div className="mt-auto px-2 pt-6 text-xs text-zinc-500">
            <p>YukNgaji &middot; Storage Utama</p>
            <Link to="/" data-testid="admin-back-catalog" className="mt-1 inline-block text-zinc-400 underline-offset-4 hover:text-white hover:underline">
              Buka katalog
            </Link>
          </div>
        </aside>

        {/* Off-canvas <1280px */}
        {open && (
          <button
            aria-label="Tutup navigasi"
            onClick={closeDrawer}
            className="fixed inset-0 z-40 bg-black/60 xl:hidden"
          />
        )}
        <aside
          ref={drawerRef}
          className={`fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col bg-[#09090B] px-3 py-4 transition-transform duration-200 xl:hidden ${
            open ? "translate-x-0 visible" : "-translate-x-full invisible"
          }`}
          aria-hidden={!open}
          inert={!open}
        >
          <div className="mb-4 flex items-center justify-between px-2 py-1">
            <Link to="/" onClick={() => setOpen(false)} aria-label="Dexter - kembali ke Home" className="flex items-center gap-2.5">
              <img src="/yn-logo.svg" alt="" className="h-8 w-8 shrink-0 object-contain" />
              <span className="text-[15px] font-bold tracking-tight">Dexter</span>
            </Link>
            <button ref={closeBtnRef} onClick={closeDrawer} aria-label="Tutup navigasi" className="rounded-full p-2 text-zinc-400 hover:bg-white/10 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          {nav}
          <div className="mt-auto px-2 pt-6 text-xs text-zinc-500">
            <p>YukNgaji &middot; Storage Utama</p>
            <Link to="/" className="mt-1 inline-block text-zinc-400 underline-offset-4 hover:text-white hover:underline">
              Buka katalog
            </Link>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-white/10 bg-[#09090B]/95 px-4 sm:px-6">
            <button
              ref={menuBtnRef}
              onClick={() => setOpen(true)}
              data-testid="admin-menu-button"
              aria-label="Buka navigasi"
              className="rounded-full p-2 text-zinc-300 hover:bg-white/10 hover:text-white xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p data-testid="admin-crumb" className="min-w-0 truncate text-sm text-zinc-500">
              Admin <span className="text-zinc-600">/</span> <span className="text-zinc-200">{NAV.find((n) => n.to !== "/admin" && location.pathname.startsWith(n.to))?.label ?? "Perlu tindakan"}</span>
            </p>
          </header>
          <main className="mx-auto w-full max-w-[1440px] flex-1 px-4 pb-16 pt-5 sm:px-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
